import { motion } from "framer-motion";
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import {
    Users,
    CalendarCheck,
    UsersRound,
    HandHeart,
    Building2,
    Sprout,
    Gauge,
    Map,
    Landmark,
    Home,
    Download,
} from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { KpiCard } from "@/components/common/KpiCard";
import { ChartCard } from "@/components/common/ChartCard";
import { IndexScorecard } from "@/components/common/IndexScorecard";
import { AreasNeedingAttention } from "@/components/common/AreasNeedingAttention";
import { ChartTooltip, axisProps, gridProps } from "@/components/common/ChartTooltip";
import { Card } from "@/components/ui/card";
import { fmt, fmtDecimal, fmtPct, WINGS, wingColor, chartColor, youthPct } from "@/lib/format";
import * as XLSX from "@/lib/excelExport";
import { withoutTechnicalExportFields } from "@/lib/excelExportFilters";
import { DOWNLOADS_ENABLED } from "@/config/features";


const formatExportHeader = (key = "") =>
    key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());

const EXCEL_CELL_LIMIT = 32767;
const EXCEL_SAFE_TEXT_LIMIT = 32000;

const safeExcelValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "number" || typeof value === "boolean") return value;
    if (value instanceof Date) return value;

    let text;
    if (typeof value === "string") {
        text = value;
    } else {
        try {
            text = JSON.stringify(value);
        } catch {
            text = String(value);
        }
    }

    if (text.length > EXCEL_SAFE_TEXT_LIMIT) {
        return `${text.slice(0, EXCEL_SAFE_TEXT_LIMIT)}… [truncated]`;
    }
    return text;
};

const flattenForExcel = (value, prefix = "", target = {}) => {
    if (value === null || value === undefined) {
        if (prefix) target[prefix] = "";
        return target;
    }

    if (Array.isArray(value)) {
        if (prefix) target[prefix] = `[${value.length} items — see separate sheet]`;
        return target;
    }

    if (typeof value === "object" && !(value instanceof Date)) {
        Object.entries(value).forEach(([key, child]) => {
            const nextPrefix = prefix ? `${prefix}.${key}` : key;
            flattenForExcel(child, nextPrefix, target);
        });
        return target;
    }

    if (prefix) target[prefix] = safeExcelValue(value);
    return target;
};

const sanitizeRows = (rows) =>
    rows.map((row) =>
        Object.fromEntries(
            Object.entries(row || {}).map(([key, value]) => [key, safeExcelValue(value)]),
        ),
    );

const enforceWorksheetCellLimits = (worksheet) => {
    Object.keys(worksheet).forEach((address) => {
        if (address.startsWith("!")) return;
        const cell = worksheet[address];
        if (!cell || cell.v === null || cell.v === undefined) return;

        if (typeof cell.v !== "string") {
            if (typeof cell.v === "object") cell.v = safeExcelValue(cell.v);
            return;
        }

        if (cell.v.length > EXCEL_CELL_LIMIT) {
            cell.v = `${cell.v.slice(0, EXCEL_SAFE_TEXT_LIMIT)}… [truncated]`;
            cell.w = cell.v;
        }
    });
};

const autoSizeWorksheet = (worksheet, rows) => {
    const keys = [...new Set(rows.flatMap((row) => Object.keys(row || {})))];
    worksheet["!cols"] = keys.map((key) => ({
        wch: Math.min(
            55,
            Math.max(
                12,
                formatExportHeader(key).length + 2,
                ...rows.map((row) => Math.min(String(row?.[key] ?? "").length + 2, 55)),
            ),
        ),
    }));
    worksheet["!autofilter"] = worksheet["!ref"] ? { ref: worksheet["!ref"] } : undefined;
};

const safeFilePart = (value = "state") =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "") || "state";

const INDEX_INFO = {
    yri: "Youth attendance share ÷ youth member share. 1.00 means youth attend in proportion to their membership.",
    wbi: "Balance across Spiritual, Education and Service activities. Higher is more balanced (0–1).",
    pvs: "Distinct activity names ÷ total activities. Ideal 0.40–0.70.",
    pci: "Concentration of activities by name. Lower is more diversified.",
    aci: "Concentration of attendance by activity name. Lower is more diversified.",
};

export default function Overview() {
    const { data } = useDashboard();
    const es = data.executive_summary;
    const stateName =
        data?.entity?.state_name ||
        data?.state_name ||
        "State Organisation";
    const annual = data.full_year_activity_summary;
    const idx = annual.analytical_indices;
    const months = data.monthly_trend.months;
    const wd = annual.wing_details || {};
    const reportingPeriod =
        data?.reporting_period?.period_label ||
        data?.reporting_period?.financial_year ||
        "the current reporting period";

    const kpis = [
        { label: "Total Units", value: fmt(es.total_units), sub: `${fmt(es.districts)} districts · ${fmt(es.samithis)} samithis`, icon: Building2, tone: "primary" },
        { label: "Approved Members", value: fmt(es.approved_members), sub: `${fmtPct(es.youth_share_pct)} youth`, icon: Users, tone: "accent" },
        { label: "Youth Members", value: fmt(es.approved_youth_members), sub: "Active young adults", icon: Sprout, tone: "success" },
        { label: "Reported Activities", value: fmt(es.reported_activities_full_year), sub: "Approved, full year", icon: CalendarCheck, tone: "primary" },
        { label: "Total Attendance", value: fmt(es.total_attendance_full_year), sub: `Avg ${fmtDecimal(es.average_attendance_per_activity)} / activity`, icon: UsersRound, tone: "accent" },
        { label: "Service Beneficiaries", value: fmt(es.service_beneficiaries_full_year), sub: "People served", icon: HandHeart, tone: "success" },
    ];

    const comboData = months.map((q) => ({
        month: q.month,
        Spiritual: q.wing_details?.spiritual?.reported_activities || 0,
        Education: q.wing_details?.education?.reported_activities || 0,
        Service: q.wing_details?.service?.reported_activities || 0,
        Others: (q.wing_details?.meetings_conferences?.reported_activities || 0) + (q.wing_details?.training?.reported_activities || 0) + (q.wing_details?.other?.reported_activities || 0),
        "Avg Attendance": q.average_attendance_per_activity,
        "Youth %": youthPct(q),
    }));

    const totalWingActs = WINGS.reduce((s, w) => s + (wd[w.code]?.reported_activities || 0), 0) || 1;
    const wingData = WINGS.map((w) => ({
        name: w.name,
        value: wd[w.code]?.reported_activities || 0,
        pct: wd[w.code]?.activity_share_pct ?? ((wd[w.code]?.reported_activities || 0) / totalWingActs) * 100,
        color: wingColor(w.code),
    })).filter((d) => d.value > 0);

    const unitBreakdown = [
        { label: "Districts", value: es.districts, icon: Map, tone: "text-chart-2" },
        { label: "Samithis", value: es.samithis, icon: Landmark, tone: "text-chart-1" },
        { label: "Bhajan Mandalis", value: es.bhajan_mandalis, icon: Home, tone: "text-chart-3" },
    ];

    const downloadAllData = async () => {
        if (!DOWNLOADS_ENABLED) return;

        const workbook = XLSX.utils.book_new();
        const usedSheetNames = new Set();

        const uniqueSheetName = (path) => {
            const base = (path || "Data")
                .split(".")
                .filter(Boolean)
                .slice(-3)
                .map(formatExportHeader)
                .join(" - ")
                .replace(/[\/*?:[\]]/g, " ")
                .slice(0, 31) || "Data";

            let name = base;
            let counter = 2;
            while (usedSheetNames.has(name)) {
                const suffix = ` ${counter++}`;
                name = `${base.slice(0, 31 - suffix.length)}${suffix}`;
            }
            usedSheetNames.add(name);
            return name;
        };

        const appendRows = (path, rows) => {
            if (!rows.length) return;
            const safeRows = sanitizeRows(rows);
            const worksheet = XLSX.utils.json_to_sheet(safeRows);
            enforceWorksheetCellLimits(worksheet);
            autoSizeWorksheet(worksheet, safeRows);
            XLSX.utils.book_append_sheet(workbook, worksheet, uniqueSheetName(path));
        };

        const metadataRows = [
            { Field: "State Organisation", Value: stateName.trim() },
            { Field: "Financial Year", Value: data?.reporting_period?.financial_year || "" },
            { Field: "Period Start", Value: data?.reporting_period?.start_date || "" },
            { Field: "Period End", Value: data?.reporting_period?.end_date || "" },
        ];
        appendRows("Export Information", metadataRows);

        const exportedArrays = new Set();
        const walk = (node, path = "State Data") => {
            if (Array.isArray(node)) {
                if (!exportedArrays.has(path)) {
                    const rows = node.map((item, index) =>
                        item && typeof item === "object" && !Array.isArray(item)
                            ? { "Row No.": index + 1, ...flattenForExcel(item) }
                            : { "Row No.": index + 1, Value: item },
                    );
                    appendRows(path, rows);
                    exportedArrays.add(path);
                }
                node.forEach((item) => walk(item, path));
                return;
            }

            if (node && typeof node === "object") {
                Object.entries(node).forEach(([key, child]) => {
                    const childPath = `${path}.${key}`;
                    if (Array.isArray(child)) {
                        walk(child, childPath);
                    } else if (child && typeof child === "object") {
                        walk(child, childPath);
                    }
                });
            }
        };

        const exportData = withoutTechnicalExportFields(data);
        Object.entries(exportData).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                walk(value, key);
            } else if (value && typeof value === "object") {
                appendRows(key, [flattenForExcel(value)]);
                walk(value, key);
            } else {
                appendRows(key, [{ Field: formatExportHeader(key), Value: value }]);
            }
        });

        const date = new Date().toISOString().slice(0, 10);
        await XLSX.writeFile(workbook, `${safeFilePart(stateName)}-all-dashboard-data-${date}.xlsx`);
    };

    return (
        <div className="space-y-8">
            {/* Hero */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-2xl bg-gradient-hero px-6 py-8 text-primary-foreground shadow-lift sm:px-9 sm:py-10"
            >
                <div className="absolute inset-0 texture-dots opacity-60" />
                <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/25 blur-3xl" />
                <div className="relative">
                    {DOWNLOADS_ENABLED && (
                        <div className="mb-6 flex justify-end">
                            <button
                                type="button"
                                onClick={downloadAllData}
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-primary-foreground/25 bg-primary-foreground/10 px-4 text-sm font-semibold text-primary-foreground shadow-sm backdrop-blur transition hover:bg-primary-foreground/20 focus:outline-none focus:ring-2 focus:ring-primary-foreground/60"
                                aria-label={`Download all ${stateName.trim()} dashboard data in Excel`}
                            >
                                <Download className="h-4 w-4" />
                                Download All Data
                            </button>
                        </div>
                    )}
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground/70">
                        Sri Sathya Sai Seva Organisations &middot; {stateName.trim()}
                    </p>
                    <h1 className="mt-2 max-w-2xl font-display text-3xl font-semibold leading-tight sm:text-[2.6rem]">
                        Insights into a year of seva, sadhana &amp; spiritual fellowship
                    </h1>
                    <p className="mt-3 max-w-xl text-sm text-primary-foreground/80 sm:text-base">
                        Organisational performance across {fmt(es.total_units)} units for {reportingPeriod}.
                    </p>
                    <div className="mt-7 grid grid-cols-2 gap-x-8 gap-y-5 sm:flex sm:flex-wrap sm:gap-x-12">
                        {[
                            { k: "Members", v: fmt(es.approved_members) },
                            { k: "Activities", v: fmt(es.reported_activities_full_year) },
                            { k: "Attendance", v: fmt(es.total_attendance_full_year) },
                            { k: "Beneficiaries", v: fmt(es.service_beneficiaries_full_year) },
                        ].map((s) => (
                            <div key={s.k}>
                                <p className="font-display text-2xl font-semibold tabular sm:text-3xl">{s.v}</p>
                                <p className="text-xs uppercase tracking-wide text-primary-foreground/70">{s.k}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* KPI grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {kpis.map((k, i) => (
                    <KpiCard key={k.label} index={i} {...k} />
                ))}
            </div>

            {/* Areas needing attention */}
            <AreasNeedingAttention items={data.areas_needing_attention} />

            {/* Charts row */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                <ChartCard
                    className="lg:col-span-3"
                    title="Monthly Momentum"
                    description="Monthly activity mix by core wing, with average attendance and youth participation"
                >
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={comboData} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="month" {...axisProps} />
                                <YAxis yAxisId="left" {...axisProps} />
                                <YAxis yAxisId="right" orientation="right" {...axisProps} unit="%" />
                                <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                <Bar yAxisId="left" dataKey="Spiritual" stackId="activities" fill={chartColor(1)} maxBarSize={48} />
                                <Bar yAxisId="left" dataKey="Education" stackId="activities" fill={chartColor(2)} maxBarSize={48} />
                                <Bar yAxisId="left" dataKey="Service" stackId="activities" fill={chartColor(3)} maxBarSize={48} />
                                <Bar yAxisId="left" dataKey="Others" stackId="activities" fill={chartColor(6)} radius={[6, 6, 0, 0]} maxBarSize={48} />
                                <Line yAxisId="left" type="monotone" dataKey="Avg Attendance" stroke={chartColor(3)} strokeWidth={2.5} dot={{ r: 3 }} />
                                <Line yAxisId="right" type="monotone" dataKey="Youth %" stroke={chartColor(2)} strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                <ChartCard
                    className="lg:col-span-2"
                    title="Activity Mix by Wing"
                    description="Share of reported activities"
                >
                    <div>
                        <div className="h-[210px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={wingData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={54} outerRadius={82} paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                                        {wingData.map((d, i) => (
                                            <Cell key={i} fill={d.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<ChartTooltip formatter={(v, e) => `${fmt(v)} (${fmtPct(e.payload.pct)})`} />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="mt-2 grid w-full grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                            {wingData.map((d) => (
                                <div key={d.name} className="flex items-center gap-2 text-xs">
                                    <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: d.color }} />
                                    <span className="truncate text-muted-foreground">{d.name}</span>
                                    <span className="ml-auto font-semibold tabular text-foreground">{fmt(d.value)}</span>
                                    <span className="w-11 text-right tabular text-muted-foreground">{fmtPct(d.pct)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </ChartCard>
            </div>

            {/* Analytical indices */}
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-xl font-semibold text-foreground">Annual Health Indices</h2>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    <IndexScorecard index={0} label="Youth Representation" value={idx.youth_representation_index.value} display={fmtDecimal(idx.youth_representation_index.value, 2)} status={idx.youth_representation_index.status} max={1.3} info={INDEX_INFO.yri} />
                    <IndexScorecard index={1} label="Wing Balance" value={idx.wing_balance_index.value} display={fmtDecimal(idx.wing_balance_index.value, 2)} status={idx.wing_balance_index.status} max={1} info={INDEX_INFO.wbi} />
                    <IndexScorecard index={2} label="Activity Diversity" value={idx.programme_variety_score.value} display={fmtDecimal(idx.programme_variety_score.value, 2)} status={idx.programme_variety_score.status} max={0.7} info={INDEX_INFO.pvs} />
                    <IndexScorecard index={3} label="Activity Concentration" value={idx.programme_concentration_index.value} display={fmtDecimal(idx.programme_concentration_index.value, 2)} status={idx.programme_concentration_index.status} max={1} info={INDEX_INFO.pci} />
                    <IndexScorecard index={4} label="Attendance Concentration" value={idx.attendance_concentration_index.value} display={fmtDecimal(idx.attendance_concentration_index.value, 2)} status={idx.attendance_concentration_index.status} max={1} info={INDEX_INFO.aci} />
                </div>
            </div>

            {/* Units composition */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {unitBreakdown.map((u, i) => (
                    <motion.div key={u.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}>
                        <Card className="flex items-center gap-4 border-border/70 bg-card p-5 shadow-card">
                            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary">
                                <u.icon className={`h-6 w-6 ${u.tone}`} />
                            </span>
                            <div>
                                <p className="text-2xl font-semibold tabular text-foreground">{fmt(u.value)}</p>
                                <p className="text-sm text-muted-foreground">{u.label}</p>
                            </div>
                        </Card>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}
